// frontend/src/features/analysis/utils/targetBand.ts

import type { TankTargetRange } from '@/hooks/useTargetRanges';
import { parseParameterKey, TARGET_RANGE_KEYS } from '../constants';

/** Y-axis bounds of a target-range band, in the chart's plotted units. */
export interface TargetBand {
  min: number;
  max: number;
}

/**
 * Resolve the target-range band for a chart, or null when none should be drawn.
 *
 * A band is only drawn when every series on the chart is the same parameter
 * (e.g. Phosphate from Manual and from Hanna). Phosphate + Nitrate on one chart
 * returns null, since one band can't belong to both. Disabled ranges and ranges
 * missing either bound are ignored.
 */
export function resolveTargetBand(
  seriesKeys: string[],
  targetRanges: TankTargetRange[] | undefined
): TargetBand | null {
  if (!targetRanges?.length) return null;

  const [param, ...rest] = seriesKeys.map((key) => parseParameterKey(key).param);
  if (!param || rest.some((p) => p !== param)) return null;

  const rangeKey = TARGET_RANGE_KEYS[param];
  if (!rangeKey) return null;

  const range = targetRanges.find((r) => r.parameterId === rangeKey && r.enabled);
  if (range?.minValue == null || range.maxValue == null || range.minValue >= range.maxValue) {
    return null;
  }
  return { min: range.minValue, max: range.maxValue };
}
